import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import './ProductDetail.css';
import AedPrice from '../components/AedPrice';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(30);
  const { addItem } = useCart();
  const { t, language, isRTL } = useLanguage();

  useEffect(() => {
    fetchProduct();
    fetchDeliveryFee();
  }, [id]);

  const fetchDeliveryFee = async () => {
    try {
      const response = await fetch('/api/settings/public');
      if (response.ok) {
        const settings = await response.json();
        if (settings.deliveryFee) {
          setDeliveryFee(parseFloat(settings.deliveryFee));
        }
      }
    } catch (error) {
      console.error('Failed to fetch delivery fee:', error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await fetch(`/api/products/${id}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = product?.enabled === false;

  const handleAddToCart = () => {
    if (isDisabled) return;
    addItem(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const formatPrice = (price) => <AedPrice amount={price} />;

  const getProductCategory = () => {
    return language === 'ar' && product.categoryAr ? product.categoryAr : product.category;
  };

  const getProductName = () => {
    return language === 'ar' && product.nameAr ? product.nameAr : product.name;
  };

  const getProductDescription = () => {
    return language === 'ar' && product.descriptionAr ? product.descriptionAr : product.description;
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container">
        <div className="not-found">
          <h2>{t('noProducts')}</h2>
          <p>{t('noProductsDesc') || 'The product you\'re looking for doesn\'t exist.'}</p>
          <Link to="/products" className="btn btn-primary">
            {t('products')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`product-detail ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <nav className="breadcrumb">
          <Link to="/">{t('home')}</Link>
          <span>/</span>
          <Link to="/products">{t('products')}</Link>
          <span>/</span>
          <span>{getProductName()}</span>
        </nav>

        <div className="product-detail-grid">
          <div className="product-gallery">
            <div className="main-image" style={{ position: 'relative' }}>
              {product.hideImage ? (
                <div style={{
                  width: '100%',
                  aspectRatio: '1',
                  background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9ca3af',
                  fontSize: '64px'
                }}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                </div>
              ) : (
                <img src={product.image} alt={getProductName()} style={{ opacity: isDisabled ? 0.6 : 1 }} />
              )}
              {product.featured && !isDisabled && !product.hideImage && <span className="featured-badge">{t('featured')}</span>}
              {isDisabled && (
                <span style={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  background: '#ef4444',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '25px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {language === 'ar' ? 'غير متوفر حالياً' : 'Currently Unavailable'}
                </span>
              )}
            </div>
          </div>

          <div className="product-details">
            <span className="product-category">{getProductCategory()}</span>
            <h1 className="product-title">{getProductName()}</h1>
            {product.onSale && product.salePrice ? (
              <div className="product-price" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span>{formatPrice(product.salePrice)}</span>
                  <span style={{ fontWeight: '700', color: '#dc2626' }}>
                    -{Math.round((1 - product.salePrice / product.price) * 100)}%
                  </span>
                </div>
                <div style={{ fontSize: '14px', fontWeight: '400', color: '#9ca3af', marginTop: '4px' }}>
                  {language === 'ar' ? 'كان' : 'Was'}: <span style={{ textDecoration: 'line-through' }}>{formatPrice(product.price)}</span>
                </div>
              </div>
            ) : (
              <p className="product-price">{formatPrice(product.price)}</p>
            )}

            <div className="product-description">
              <h3>{t('description')}</h3>
              <p>{getProductDescription()}</p>
            </div>

            <div className="product-stock">
              {isDisabled ? (
                <span className="out-of-stock" style={{ background: '#fef2f2', color: '#ef4444' }}>
                  {language === 'ar' ? 'غير متوفر حالياً' : 'Currently Unavailable'}
                </span>
              ) : product.stock > 0 ? (
                <span className="in-stock">{t('inStock')}</span>
              ) : (
                <span className="out-of-stock">{t('outOfStock')}</span>
              )}
            </div>

            <div className="add-to-cart-section">
              <div className="quantity-selector">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  max={product.stock}
                />
                <button
                  onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                  disabled={quantity >= product.stock}
                >
                  +
                </button>
              </div>

              {isDisabled ? (
                <button
                  className="btn btn-lg"
                  disabled
                  style={{
                    background: '#9ca3af',
                    color: 'white',
                    cursor: 'not-allowed',
                    width: '100%'
                  }}
                >
                  {language === 'ar' ? 'غير متوفر حالياً' : 'Currently Unavailable'}
                </button>
              ) : (
                <button
                  className={`btn btn-primary btn-lg ${added ? 'added' : ''}`}
                  onClick={handleAddToCart}
                  disabled={product.stock === 0}
                >
                  {added ? t('addedToCart') : t('addToCart')}
                </button>
              )}
            </div>

            <div className="product-features">
              <div className="feature">
                <span className="feature-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg></span>
                <span>{language === 'ar' ? 'توصيل لجميع الإمارات - ' : 'Delivery to all UAE Emirates - '}<AedPrice amount={parseFloat(deliveryFee)} decimals={0} /></span>
              </div>
              <div className="feature">
                <span className="feature-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22V2"/><path d="M5 12c2-3 5-5 7-5s5 2 7 5"/><path d="M5 18c2-3 5-5 7-5s5 2 7 5"/></svg></span>
                <span>{t('glutenFree')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
